import { useState } from "react";
import { api, ApiError } from "../lib/api.ts";
import { relativeDate } from "../lib/format.ts";
import { useGet, useSession } from "../lib/session.tsx";
import type { Invite, Member, RoleDef } from "../lib/types.ts";

const BUILTIN: { name: string; label: string; hint: string }[] = [
  { name: "dono", label: "Dono", hint: "tudo, inclusive membros" },
  { name: "construtor", label: "Construtor", hint: "estrutura, telas e dados" },
  { name: "operador", label: "Operador", hint: "todos os dados" },
];

/** Who is in the store and with which roles. Only owners get here. */
export function Members() {
  const s = useSession();
  const members = useGet<{ members: Member[]; invites: Invite[] }>("/v1/members");
  const roles = useGet<{ roles: RoleDef[] }>("/v1/roles");
  const [email, setEmail] = useState("");
  const [picked, setPicked] = useState<string[]>(["operador"]);
  const [editing, setEditing] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const all = [...BUILTIN, ...(roles.data?.roles ?? []).map((r) => ({ name: r.name, label: r.label, hint: r.description ?? "papel da loja" }))];
  const label = (n: string) => all.find((r) => r.name === n)?.label ?? n;

  async function run(fn: () => Promise<unknown>, done: string | ((r: unknown) => string)) {
    setBusy(true);
    setErr(null);
    try {
      const r = await fn();
      s.toast(typeof done === "string" ? done : done(r));
      members.reload();
      return true;
    } catch (e) {
      setErr(e instanceof ApiError ? [e.message, ...e.details.map((d) => d.message)].filter((x, i, a) => a.indexOf(x) === i).join(" ") : "Erro");
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ maxWidth: 820 }}>
      <div className="topbar">
        <h1>Membros</h1>
      </div>
      <form
        className="card stack"
        onSubmit={async (e) => {
          e.preventDefault();
          const to = email.trim();
          const done = (r: unknown) => ((r as { invite?: Invite }).invite ? `Convite enviado para ${to}.` : "Pessoa adicionada. Avisamos por e-mail.");
          if (await run(() => api("POST", "/v1/members", { email: to, roles: picked }), done)) setEmail("");
        }}
      >
        <strong>Adicionar pessoa</strong>
        <p className="muted" style={{ margin: 0 }}>
          Quem já tem conta entra na loja na hora. Quem ainda não tem recebe um convite por e-mail e entra na loja ao criar a conta.
        </p>
        <label className="fld">
          E-mail
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="pessoa@empresa.com.br" />
        </label>
        <RolePicker all={all} value={picked} onChange={setPicked} />
        <div>
          <button className="btn primary" disabled={busy || !email || !picked.length}>
            Adicionar ou convidar
          </button>
        </div>
      </form>
      {err && <p className="error">{err}</p>}
      {members.error && <p className="error">{members.error.message}</p>}
      {!!members.data?.invites.length && (
        <div className="card list">
          <strong>Convites pendentes</strong>
          {members.data.invites.map((i) => (
            <div key={i.id} className="list-item" style={{ flexWrap: "wrap" }}>
              <span style={{ fontWeight: 600 }}>{i.email}</span>
              {i.roles.map((r) => (
                <span key={r} className="chip gray">
                  {label(r)}
                </span>
              ))}
              <span className="spacer" />
              <span className="muted" style={{ fontSize: 12.5 }}>
                convite de {i.invitedBy.split(" ")[0]}, {relativeDate(i.createdAt)}
              </span>
              <button className="btn sm" disabled={busy} onClick={() => run(() => api("POST", `/v1/invites/${i.id}/resend`), `Convite reenviado para ${i.email}.`)}>
                Reenviar
              </button>
              <button
                className="btn sm danger"
                disabled={busy}
                onClick={() => confirm(`Cancelar o convite de ${i.email}? O link do e-mail deixa de valer.`) && run(() => api("DELETE", `/v1/invites/${i.id}`), "Convite cancelado.")}
              >
                Cancelar
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="card list">
        <strong>Pessoas nesta loja</strong>
        {members.data?.members.map((m) => (
          <div key={m.accountId} className="list-item" style={{ flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600 }}>{m.name}</span>
            <span className="muted">{m.email}</span>
            {editing !== m.accountId && m.roles.map((r) => <span key={r} className="chip gray">{label(r)}</span>)}
            <span className="spacer" />
            <span className="muted" style={{ fontSize: 12.5 }}>
              desde {relativeDate(m.createdAt)}
            </span>
            {editing !== m.accountId && (
              <>
                <button className="btn sm" onClick={() => setEditing(m.accountId)}>
                  Papéis
                </button>
                {m.accountId !== s.account.id && (
                  <button
                    className="btn sm danger"
                    disabled={busy}
                    onClick={() => confirm(`Tirar ${m.name} da loja? O acesso acaba na hora, inclusive o da IA dessa pessoa.`) && run(() => api("DELETE", `/v1/members/${m.accountId}`), "Pessoa removida.")}
                  >
                    Remover
                  </button>
                )}
              </>
            )}
            {editing === m.accountId && (
              <EditRoles
                all={all}
                initial={m.roles}
                busy={busy}
                onCancel={() => setEditing(null)}
                onSave={async (roles) => {
                  if (await run(() => api("PUT", `/v1/members/${m.accountId}`, { roles }), "Papéis alterados.")) {
                    setEditing(null);
                    if (m.accountId === s.account.id) await s.reloadTenants();
                  }
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function EditRoles({ all, initial, busy, onSave, onCancel }: { all: { name: string; label: string; hint: string }[]; initial: string[]; busy: boolean; onSave: (r: string[]) => void; onCancel: () => void }) {
  const [value, setValue] = useState(initial);
  return (
    <div className="stack" style={{ flexBasis: "100%" }}>
      <RolePicker all={all} value={value} onChange={setValue} />
      <div className="row">
        <button className="btn primary sm" disabled={busy || !value.length} onClick={() => onSave(value)}>
          Salvar
        </button>
        <button className="btn sm" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

function RolePicker({ all, value, onChange }: { all: { name: string; label: string; hint: string }[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="fld">
      Papéis
      <div className="checks">
        {all.map((r) => {
          const on = value.includes(r.name);
          return (
            <label key={r.name} className={on ? "on" : ""} title={r.hint}>
              <input type="checkbox" checked={on} onChange={() => onChange(on ? value.filter((x) => x !== r.name) : [...value, r.name])} />
              {r.label}
            </label>
          );
        })}
      </div>
    </div>
  );
}
